import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import usePageTitle from "../../core/hooks/UsePageTitle";
import DefaultLayout from "../layouts/default/Default.layout";
import {
  Col,
  Form,
  Row,
  Button,
  notification,
  Switch,
  Tag,
  Drawer,
  Space,
  Select,
  Popconfirm,
  InputNumber,
} from "antd";
import { Product } from "../../sdk/@types";
import Table from "../components/Table/Table";
import { Column, useTable } from "react-table";
import { useEffect, useMemo, useState } from "react";
import useProduct from "../../core/hooks/UseProduct";
import useCertame from "../../core/hooks/UseCertame";
import useUsuario from "../../core/hooks/UseUsuario";
import { useSimulado } from "../../core/hooks/useSimulado";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  StarFilled,
} from "@ant-design/icons";
import { z } from "zod";
import ProductService from "../../sdk/services/ProductService";

/* ===================== SCHEMA ===================== */

const productSchema = z
  .object({
    id: z.string().optional(),
    title: z
      .string({ error: "O título é obrigatório" })
      .min(3, "O título deve ter pelo menos 3 caracteres"),
    description: z
      .string({ error: "A descrição é obrigatória" })
      .min(6, "A descrição deve ter pelo menos 6 caracteres"),
    price: z
      .number({ error: "O preço é obrigatório" })
      .min(0, "O preço não pode ser negativo"),
    amountOfDays: z
      .number({ error: "A quantidade de dias é obrigatória" })
      .int("Deve ser um número inteiro")
      .min(1, "Deve ter pelo menos 1 dia"),
    selectionProcessIds: z.array(z.string()),
    features: z.array(z.string()).min(1, "Adicione pelo menos um benefício"),
    mockTestId: z.string().optional(),
    popular: z.boolean(),
    disabled: z.boolean(),
    createdAt: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const hasCertames = data.selectionProcessIds.length > 0;
    const hasSimulado = !!data.mockTestId;
    if (!hasCertames && !hasSimulado) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["selectionProcessIds"],
        message: "Vincule ao menos um certame ou um simulado",
      });
    }
    if (hasCertames && hasSimulado) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["mockTestId"],
        message: "Escolha apenas certame ou simulado, não ambos",
      });
    }
  });

type ProductFormInput = z.input<typeof productSchema>;
type ProductFormOutput = z.output<typeof productSchema>;

/* ===================== HELPERS ===================== */

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 mt-6 mb-4">
      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5f6f65]">
        {label}
      </span>
      <div className="flex-1 border-t border-gray-100" />
    </div>
  );
}

const situationStyle: Record<string, string> = {
  disponivel: "bg-green-100 text-green-700",
  edicao: "bg-amber-100 text-amber-700",
};

const situationLabel: Record<string, string> = {
  disponivel: "Disponível",
  edicao: "Em edição",
};

/* ===================== COMPONENT ===================== */

export default function ProdutoView() {
  usePageTitle("Produto");

  const { products, getProducts } = useProduct();
  const { certames, getCertames } = useCertame();
  const { usuario } = useUsuario();
  const { simulados, fetchSimulados } = useSimulado();

  const [drawerMode, setDrawerMode] = useState<null | "new" | string>(null);
  const isDrawerOpen = drawerMode !== null;
  const isEditing = drawerMode !== null && drawerMode !== "new";

  const [linkedType, setLinkedType] = useState<"certame" | "simulado">("certame");
  const [featureInput, setFeatureInput] = useState("");

  /* ===================== FORM ===================== */

  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProductFormInput>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      id: "",
      title: "",
      description: "",
      price: 0,
      amountOfDays: 30,
      selectionProcessIds: [],
      features: [],
      mockTestId: "",
      popular: false,
      disabled: false,
    },
  });

  const features = watch("features") ?? [];

  /* ===================== EFFECTS ===================== */

  useEffect(() => {
    getProducts();
    getCertames();
    fetchSimulados();
  }, [getProducts, getCertames, fetchSimulados]);

  const publishedSimulados = useMemo(
    () =>
      (simulados ?? []).filter(
        (s) => s.situation === "disponivel" || s.situation === "edicao",
      ),
    [simulados],
  );

  /* ===================== FEATURES BUILDER ===================== */

  const addFeature = () => {
    const trimmed = featureInput.trim();
    if (!trimmed) return;
    setValue("features", [...features, trimmed], { shouldValidate: true });
    setFeatureInput("");
  };

  const removeFeature = (idx: number) => {
    setValue(
      "features",
      features.filter((_, i) => i !== idx),
      { shouldValidate: true },
    );
  };

  /* ===================== DRAWER HELPERS ===================== */

  const openNew = () => {
    reset({
      id: "",
      title: "",
      description: "",
      price: 0,
      amountOfDays: 30,
      selectionProcessIds: [],
      features: [],
      mockTestId: "",
      popular: false,
      disabled: false,
    });
    setFeatureInput("");
    setLinkedType("certame");
    setDrawerMode("new");
  };

  const openEdit = (row: Product.Product) => {
    const type = row.mockTestId ? "simulado" : "certame";
    setLinkedType(type);
    reset({
      id: row.id ?? "",
      title: row.title ?? "",
      description: row.description ?? "",
      price: row.price ?? 0,
      amountOfDays: row.amountOfDays ?? 30,
      selectionProcessIds: row.selectionProcessIds ?? [],
      features: row.features ?? [],
      mockTestId: row.mockTestId ?? "",
      popular: row.popular ?? false,
      disabled: row.disabled ?? false,
      createdAt: row.createdAt,
    });
    setFeatureInput("");
    setDrawerMode(row.id ?? "new");
  };

  const closeDrawer = () => {
    setDrawerMode(null);
    setFeatureInput("");
    setLinkedType("certame");
    reset();
  };

  const switchLinkedType = (type: "certame" | "simulado") => {
    setLinkedType(type);
    if (type === "certame") {
      setValue("mockTestId", "", { shouldValidate: false });
    } else {
      setValue("selectionProcessIds", [], { shouldValidate: false });
    }
  };

  /* ===================== DELETE ===================== */

  const handleDelete = async (row: Product.Product) => {
    try {
      await ProductService.delete(row);
      notification.success({
        message: "Sucesso",
        description: `Produto "${row.title}" removido com sucesso.`,
      });
      getProducts();
    } catch (error) {
      notification.error({
        message: "Erro ao excluir",
        description: error instanceof Error ? error.message : "Erro inesperado",
      });
    }
  };

  /* ===================== TABLE ===================== */

  const columns: Column<Product.Product>[] = useMemo(
    () => [
      { Header: "Título", accessor: "title" },
      {
        Header: "Preço",
        Cell: ({ row }) =>
          row.original.price != null
            ? `R$ ${row.original.price.toFixed(2)}`
            : "—",
      },
      { Header: "Dias", accessor: "amountOfDays" },
      {
        Header: "Popular",
        Cell: ({ row }) =>
          row.original.popular ? (
            <StarFilled className="text-amber-400" />
          ) : (
            <span className="text-gray-300">—</span>
          ),
      },
      {
        Header: "Status",
        Cell: ({ row }) => {
          const disabled = Boolean(row.original.disabled);
          return (
            <Tag color={disabled ? "red" : "green"}>
              {disabled ? "DESATIVADO" : "ATIVO"}
            </Tag>
          );
        },
      },
      {
        Header: "Ações",
        Cell: ({ row }) => (
          <Space>
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(row.original)}
            >
              Editar
            </Button>
            <Popconfirm
              title="Excluir produto"
              description={`Tem certeza que deseja excluir "${row.original.title}"?`}
              okText="Sim, excluir"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(row.original)}
            >
              <Button
                type="default"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                Excluir
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [certames],
  );

  const instance = useTable<Product.Product>({
    columns,
    data: products ?? [],
  });

  /* ===================== SUBMIT ===================== */

  const onSubmit = async (data: ProductFormInput) => {
    try {
      const parsed: ProductFormOutput = productSchema.parse(data);
      if (isEditing) {
        await ProductService.update(parsed);
        notification.success({
          message: "Sucesso",
          description: "Produto atualizado com sucesso.",
        });
      } else {
        await ProductService.create(parsed);
        notification.success({
          message: "Sucesso",
          description: "Produto criado com sucesso.",
        });
      }
      closeDrawer();
      getProducts();
    } catch (error) {
      if (error instanceof z.ZodError) {
        notification.error({
          message: "Erro de validação",
          description: error.issues.map((e) => e.message).join(", "),
        });
      } else if (error instanceof Error) {
        notification.error({ message: "Erro", description: error.message });
      } else {
        notification.error({ message: "Erro", description: "Erro inesperado" });
      }
    }
  };

  /* ===================== UI ===================== */

  return (
    <DefaultLayout>
      <div>
        <Row gutter={16}>
          <div className="justify-start mb-2 border-b p-2">
            <p className="text-2xl font-bold text-gray-500">Produto</p>
            <p className="text-sm font-normal text-gray-400">
              Manutenção do cadastro de produtos
            </p>
          </div>
        </Row>

        <Row gutter={16}>
          <div className="flex justify-end p-3 w-full">
            <Button type="primary" onClick={openNew} icon={<PlusOutlined />}>
              Novo Produto
            </Button>
          </div>
        </Row>

        <Row gutter={16}>
          <Col span={24} style={{ marginTop: "20px" }}>
            <Table instance={instance} />
          </Col>
        </Row>
      </div>

      {/* ── DRAWER ── */}
      <Drawer
        title={isEditing ? "Editar Produto" : "Novo Produto"}
        size="large"
        onClose={closeDrawer}
        open={isDrawerOpen}
        styles={{ body: { paddingBottom: 80 } }}
        extra={
          <Space>
            <Button onClick={closeDrawer}>Cancelar</Button>
            <Button
              type="primary"
              loading={isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              {isEditing ? "Salvar alterações" : "Salvar"}
            </Button>
          </Space>
        }
      >
        <Form layout="vertical">
          <form id="produto-form" onSubmit={handleSubmit(onSubmit)}>

            {/* ── INFORMAÇÕES BÁSICAS ── */}
            <SectionHeader label="Informações básicas" />

            <Row gutter={16} align="top">
              <Col span={14}>
                <Form.Item
                  label="Título"
                  validateStatus={errors.title ? "error" : ""}
                  help={errors.title?.message || " "}
                >
                  <input
                    {...register("title")}
                    className="w-full border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 h-[40px] text-sm focus:outline-none focus:ring-2 focus:ring-[#2ecc71] focus:border-[#2ecc71] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </Form.Item>
              </Col>

              <Col span={5}>
                <Form.Item label="Popular">
                  <Controller
                    control={control}
                    name="popular"
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        checkedChildren="Sim"
                        unCheckedChildren="Não"
                      />
                    )}
                  />
                </Form.Item>
              </Col>

              <Col span={5}>
                <Form.Item label="Status">
                  <Controller
                    control={control}
                    name="disabled"
                    render={({ field }) => (
                      <Switch
                        checked={!field.value}
                        onChange={(val) => field.onChange(!val)}
                        checkedChildren="Ativo"
                        unCheckedChildren="Inativo"
                      />
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* ── DESCRIÇÃO ── */}
            <SectionHeader label="Descrição" />

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  validateStatus={errors.description ? "error" : ""}
                  help={errors.description?.message || " "}
                >
                  <textarea
                    {...register("description")}
                    rows={3}
                    placeholder="Descreva o produto em poucas frases..."
                    className="w-full border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#2ecc71] focus:border-[#2ecc71] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-400"
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* ── PRECIFICAÇÃO ── */}
            <SectionHeader label="Precificação" />

            <Row gutter={16} align="top">
              <Col span={12}>
                <Form.Item
                  label="Preço (R$)"
                  validateStatus={errors.price ? "error" : ""}
                  help={errors.price?.message || " "}
                >
                  <Controller
                    control={control}
                    name="price"
                    render={({ field }) => (
                      <InputNumber
                        {...field}
                        className="w-full"
                        min={0}
                        precision={2}
                        prefix="R$"
                      />
                    )}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Dias de acesso"
                  validateStatus={errors.amountOfDays ? "error" : ""}
                  help={errors.amountOfDays?.message || " "}
                >
                  <Controller
                    control={control}
                    name="amountOfDays"
                    render={({ field }) => (
                      <InputNumber
                        {...field}
                        className="w-full"
                        min={1}
                        precision={0}
                        addonAfter="dias"
                      />
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* ── VÍNCULO ── */}
            <SectionHeader label="Vínculo do produto" />

            {/* Toggle */}
            <div className="flex gap-0 mb-4 rounded-lg border border-gray-200 overflow-hidden w-fit">
              {(["certame", "simulado"] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => switchLinkedType(type)}
                  className={[
                    "px-5 py-2 text-sm font-semibold transition-all",
                    linkedType === type
                      ? "bg-[#2ecc71] text-white shadow-inner"
                      : "bg-white text-gray-500 hover:bg-gray-50",
                  ].join(" ")}
                >
                  {type === "certame" ? "Certame" : "Simulado"}
                </button>
              ))}
            </div>

            {/* Certames */}
            {linkedType === "certame" && (
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    validateStatus={errors.selectionProcessIds ? "error" : ""}
                    help={
                      (errors.selectionProcessIds as any)?.message || " "
                    }
                  >
                    <Controller
                      control={control}
                      name="selectionProcessIds"
                      render={({ field }) => (
                        <Select
                          {...field}
                          mode="multiple"
                          showSearch
                          className="w-full"
                          placeholder="Selecione os certames incluídos"
                          options={(certames ?? []).map((c) => ({
                            label: `${c.abbreviation} — ${c.description}`,
                            value: c.id,
                          }))}
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {/* Simulado */}
            {linkedType === "simulado" && (
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    validateStatus={errors.mockTestId ? "error" : ""}
                    help={errors.mockTestId?.message || " "}
                  >
                    <Controller
                      control={control}
                      name="mockTestId"
                      render={({ field }) => (
                        <Select
                          value={field.value || undefined}
                          onChange={field.onChange}
                          showSearch
                          allowClear
                          className="w-full"
                          placeholder="Selecione um simulado publicado..."
                          optionLabelProp="label"
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toString()
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {publishedSimulados.map((s) => (
                            <Select.Option
                              key={s.id}
                              value={s.id}
                              label={s.description}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm truncate">
                                  {s.description}
                                </span>
                                <span
                                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${
                                    situationStyle[s.situation ?? ""] ??
                                    "bg-gray-100 text-gray-500"
                                  }`}
                                >
                                  {situationLabel[s.situation ?? ""] ??
                                    s.situation}
                                </span>
                              </div>
                            </Select.Option>
                          ))}
                        </Select>
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {/* ── BENEFÍCIOS ── */}
            <SectionHeader label="Benefícios" />

            <Row gutter={16}>
              <Col span={24}>
                {errors.features && (
                  <p className="text-red-500 text-xs mb-2">
                    {errors.features.message}
                  </p>
                )}

                {/* Input row */}
                <div className="flex gap-2 mb-3">
                  <input
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addFeature();
                      }
                    }}
                    placeholder="Ex: Acesso a questões comentadas"
                    className="flex-1 border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 h-[38px] text-sm focus:outline-none focus:ring-2 focus:ring-[#2ecc71] focus:border-[#2ecc71] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-400"
                  />
                  <button
                    type="button"
                    onClick={addFeature}
                    disabled={!featureInput.trim()}
                    className="h-[38px] px-4 rounded-lg bg-[#2ecc71] hover:bg-[#27ae60] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors shrink-0"
                  >
                    Adicionar
                  </button>
                </div>

                {/* Pills */}
                {features.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 rounded-xl bg-gray-50 border border-gray-100 min-h-[48px]">
                    {features.map((f, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-800 text-xs font-medium px-3 py-1 rounded-full"
                      >
                        <span className="text-[#2ecc71] font-bold">✔</span>
                        {f}
                        <button
                          type="button"
                          onClick={() => removeFeature(idx)}
                          className="ml-0.5 text-green-500 hover:text-red-500 transition-colors leading-none"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {features.length === 0 && (
                  <div className="flex items-center justify-center h-[48px] rounded-xl bg-gray-50 border border-dashed border-gray-200">
                    <span className="text-xs text-gray-400">
                      Nenhum benefício adicionado ainda
                    </span>
                  </div>
                )}
              </Col>
            </Row>

          </form>
        </Form>
      </Drawer>
    </DefaultLayout>
  );
}
